import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { LOGO_URL } from "../data/site";

const links = [
  { label: "Work", to: "/#work" },
  {
    label: "Services",
    to: "/#services",
    children: [
      { label: "All Services", to: "/#services" },
      { label: "Logo Designs", to: "/logo-types" },
      { label: "Business Card Designs", to: "/business-cards" },
      { label: "Abstract Art", to: "/abstract-art" },
      { label: "Commission Portraits", to: "/commission-portraits" },
      { label: "Comic Book Designs", to: "/comic-books" },
      { label: "Children's Book Designs", to: "/childrens-books" },
      { label: "Custom Illustrations", to: "/custom-illustrations" },
      { label: "Special Projects", to: "/special-projects" },
    ],
  },
  { label: "Contact", to: "/contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleAnchor = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      const id = to.split("#")[1];
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 120);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
    }
  };

  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-50 bg-[#F3F4F6]/85 backdrop-blur-md border-b border-[#E3E5E9]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 sm:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center group"
          onClick={() => setOpen(false)}
        >
          <img
            src={LOGO_URL}
            alt="My Gifted Hands logo"
            className="h-12 sm:h-14 w-auto object-contain"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) =>
            l.children ? (
              <div key={l.label} className="relative group">
                <button
                  data-testid={`nav-${l.label.toLowerCase()}`}
                  onClick={() => handleAnchor(l.to)}
                  className="flex items-center gap-1 text-sm font-bold uppercase tracking-[0.15em] hover:text-[#3E6FA8] transition-colors"
                >
                  {l.label}
                  <ChevronDown size={14} strokeWidth={2.5} className="mt-0.5 transition-transform group-hover:rotate-180" />
                </button>
                <div className="absolute left-0 top-full pt-3 hidden group-hover:block">
                  <div className="min-w-[210px] brutal-border bg-white brutal-shadow-sm p-2">
                    {l.children.map((c) => (
                      <button
                        key={c.label}
                        data-testid={`nav-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => handleAnchor(c.to)}
                        className="w-full text-left text-sm font-semibold px-3 py-2 rounded-xl hover:bg-[#EAF1F8] hover:text-[#3E6FA8] transition-colors"
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <button
                key={l.label}
                data-testid={`nav-${l.label.toLowerCase()}`}
                onClick={() => handleAnchor(l.to)}
                className="text-sm font-bold uppercase tracking-[0.15em] hover:text-[#3E6FA8] transition-colors"
              >
                {l.label}
              </button>
            )
          )}
          <button
            data-testid="nav-start-order"
            onClick={() => handleAnchor("/contact")}
            className="brutal-border brutal-shadow-sm brutal-press bg-[#3E6FA8] text-white font-extrabold uppercase tracking-wider text-sm px-5 py-2"
          >
            Start Order
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden brutal-border bg-white p-2"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} strokeWidth={2.5} /> : <Menu size={22} strokeWidth={2.5} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden border-t-2 border-[#1F2530] bg-[#F3F4F6]">
          <div className="px-6 py-4 flex flex-col gap-4">
            {links.map((l) => (
              <div key={l.label} className="flex flex-col gap-3">
                <button
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  onClick={() => handleAnchor(l.to)}
                  className="text-left text-lg font-bold uppercase tracking-wide"
                >
                  {l.label}
                </button>
                {l.children && (
                  <div className="flex flex-col gap-2 pl-4 border-l-2 border-[#E3E5E9]">
                    {l.children.map((c) => (
                      <button
                        key={c.label}
                        data-testid={`mobile-nav-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => handleAnchor(c.to)}
                        className="text-left text-sm font-semibold text-[#1F2530]/80 hover:text-[#3E6FA8]"
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <button
              data-testid="mobile-start-order"
              onClick={() => handleAnchor("/contact")}
              className="brutal-border brutal-shadow-sm bg-[#3E6FA8] text-white font-extrabold uppercase tracking-wider px-5 py-3"
            >
              Start Order
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
